import React, { useMemo, useState } from 'react'
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/react'
import './audit.scss'
import { formatCount } from '../sso-workspace/format'
import { SEARCH_FIELD_PROPS } from '../component/search-field-props'

// A combo's list at its tallest (px); shorter when the window has less room.
const LIST_MAX = 280

// Where a combo's list opens (MultiCombo, OrgPicker): under the box, as
// wide as it, or over it when there is more room there (shared/component/
// floating.js's flip and size), never taller than the window has room for.
// It is fixed to the window, so a box that scrolls -- the Audit log's sticky
// filter column (owner 2026-10-09), a drawer -- never clips it. It is not
// in a portal: it stays beside its input, in the workspace's tokens, and the
// input's focus and mousedown handling work as before.
export const useComboList = open => {
  const { refs, floatingStyles } = useFloating({
    open,
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      size({
        padding: 8,
        apply ({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.max(120, Math.min(LIST_MAX, availableHeight))}px`
          })
        }
      })
    ]
  })
  return { anchor: refs.setReference, list: refs.setFloating, style: floatingStyles }
}

// Live-search multi-select with groups and counts (spec §5 MultiCombo).
// ARIA combobox + listbox: ArrowUp/Down move, Enter toggles, Escape closes,
// Backspace in an empty box removes the last pick. The mouse uses mousedown
// so the input keeps focus.
//
// groups: [{ label, options: [{ value, label, sub, count, disabled }] }]. A
// disabled option is shown with its own count and can't be picked (the
// actor facet's "EducationXR support" and "Unattributed change" buckets).
// A picked value with no option (from the URL) shows as its raw value.
export const MultiCombo = ({ id, label, groups, value, onChange, placeholder }) => {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const all = useMemo(() => groups.flatMap(g => g.options), [groups])
  const labelOf = v => (all.find(o => o.value === v) || { label: v }).label
  const needle = text.trim().toLowerCase()
  const shown = useMemo(
    () =>
      groups
        .map(g => ({ ...g, options: g.options.filter(o => !needle || `${o.label} ${o.sub || ''}`.toLowerCase().includes(needle)) }))
        .filter(g => g.options.length),
    [groups, needle]
  )
  const flat = shown.flatMap(g => g.options)
  const pickable = flat.filter(o => !o.disabled)
  const current = open ? pickable[active] : undefined

  const toggle = v => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v])
  const place = useComboList(open)

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive(a => Math.min(pickable.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(0, a - 1))
    } else if (e.key === 'Enter' && current) {
      e.preventDefault()
      toggle(current.value)
      setText('')
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
    } else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1))
  }

  const listID = `${id}-list`
  const optID = o => `${id}-opt-${flat.indexOf(o)}`
  return (
    <div className="au-combo" ref={place.anchor}>
      {value.length > 0 &&
        <div className="au-picked">
          {value.map(v =>
            <span className="au-tag" key={v}>
              {labelOf(v)}
              <button type="button" aria-label={`Remove ${labelOf(v)}`} onClick={() => toggle(v)}>×</button>
            </span>)}
        </div>}
      <input
        {...SEARCH_FIELD_PROPS}
        id={id}
        name={id}
        className="form-control form-control-sm"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listID}
        aria-autocomplete="list"
        aria-activedescendant={current ? optID(current) : undefined}
        placeholder={placeholder}
        value={text}
        onChange={e => {
          setText(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false)
          setActive(-1)
        }}
        onKeyDown={onKeyDown}
      />
      {open &&
        <div className="au-combo-list" ref={place.list} style={place.style} role="listbox" id={listID} aria-label={label} aria-multiselectable="true">
          {shown.length === 0 && <div className="au-combo-hint">No matches</div>}
          {shown.map(g =>
            <div key={g.label} role="group" aria-label={g.label}>
              <div className="au-combo-grp" aria-hidden="true">{g.label}</div>
              {g.options.map(o => {
                const picked = value.includes(o.value)
                return (
                  <div
                    key={o.value}
                    id={optID(o)}
                    role="option"
                    aria-selected={picked}
                    aria-disabled={o.disabled ? true : undefined}
                    className={`au-opt${o === current ? ' au-active' : ''}${o.count === 0 ? ' au-zero' : ''}${o.disabled ? ' au-opt-off' : ''}`}
                    onMouseDown={e => {
                      e.preventDefault()
                      if (!o.disabled) toggle(o.value)
                    }}
                  >
                    {!o.disabled && <input type="checkbox" readOnly checked={picked} tabIndex={-1} aria-hidden="true" />}
                    <span className="au-opt-text">
                      {o.label}
                      {o.sub && <span className="au-opt-sub"> {o.sub}</span>}
                    </span>
                    {o.count !== undefined && <span className="au-n">{formatCount(o.count)}</span>}
                  </div>
                )
              })}
            </div>)}
        </div>}
    </div>
  )
}
